import { FC, useMemo, useState, useContext } from 'react';
import {
  Box,
  FormControlLabel,
  IconButton,
  Typography,
  Button,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { DropResult } from 'react-beautiful-dnd';
import { Table, Link } from '../../../../components';
import { MobilePunchlistTodoList } from './mobile-punchlist-todo-list';
import { UserContext, VendorPageContext } from '../../../../context';
import { IPunchlistTodoItem, IPunchlistDetail } from '../../../../models';
import {
  updatePunchlistTodo,
  updatePunchlistTodoSort,
  deletePunchlistItem
} from '../../../../fetch';
import { useSnackbar } from 'notistack';
import { Delete, DragIndicator, ChatBubble, AttachFile } from '@mui/icons-material';
import { formatDate, truncString } from '../../../../helpers';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../../../styles/theme';

interface IPunchlistDetailsTodoList {
  punchlistDetail: IPunchlistDetail;
  route?: string;
  handleTitleClick: (val: IPunchlistTodoItem) => void;
  setPunchlistDetails: (val: IPunchlistDetail) => void;
  setIsDeleting: (val: boolean) => void;
  hasAppliedFilters?: boolean;
  fetchPunchlistDetails: (showLoader?: boolean, resetFilters?: boolean) => Promise<NodeJS.Timeout>;
}

export const PunchlistDetailsTodoList: FC<IPunchlistDetailsTodoList> = ({
  punchlistDetail,
  route,
  handleTitleClick,
  setPunchlistDetails,
  setIsDeleting,
  hasAppliedFilters,
  fetchPunchlistDetails
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(`(max-width: 1024px)`);
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setSaving] = useState<{
    [id: number]: boolean;
  }>({});
  const { vendorName } = useContext(VendorPageContext);
  const { isAdmin, isField, isFieldAdmin, isVendor } = useContext(UserContext);
  const allowedRole = isAdmin || isField || isFieldAdmin || isVendor ? true : false;

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || hasAppliedFilters) return;

    const currentItems = [...punchlistDetail.punchListItems];
    const [reorderedItem] = currentItems.splice(result.source.index, 1);
    currentItems.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update the UI
    const updatedPunchlistDetail = {
      ...punchlistDetail,
      punchListItems: currentItems
    };
    setPunchlistDetails(updatedPunchlistDetail);

    try {
      await updatePunchlistTodoSort(
        punchlistDetail.punchListId,
        currentItems.map((item, index) => ({
          punchlistItemId: item.punchListItemId,
          sortOrder: index
        }))
      );
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error saving punchlist, please try again.', {
        variant: 'error'
      });

      // Revert to the original order in case of error
      setPunchlistDetails(punchlistDetail);
    }
  };

  const getActionTitle = (item: IPunchlistTodoItem): string => {
    let action = 'Assigned To:';
    if (item.itemStatuses === 'Complete') {
      action = 'Completed By:';
    }
    return action;
  };

  const handleCheckboxComplete = async (checked: boolean, original: IPunchlistTodoItem) => {
    setSaving({
      [original.punchListItemId]: true
    });

    try {
      if (!checked) {
        await updatePunchlistTodoSort(
          punchlistDetail.punchListId,
          punchlistDetail.punchListItems.map((item, index) => ({
            punchlistItemId: item.punchListItemId,
            sortOrder: index
          }))
        );
      }
      await updatePunchlistTodo(original.punchListItemId, {
        isComplete: checked,
        vendorSubscribers: original.vendorSubscribers.map(v => v.vendorId),
        photoGuids: original?.photoGuids ?? [],
        description: original.description,
        partnerId: original.partnerId,
        assignedTo: original.assignedTo,
        dueDate: original.dueDate,
        sortOrder: original.sortOrder,
        subscribers: original?.subscribers ?? []
      });
      await fetchPunchlistDetails(false, false);

      enqueueSnackbar('Item updated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error saving punchlist, please try again.', {
        variant: 'error'
      });
    } finally {
      setSaving({
        [original.punchListItemId]: false
      });
    }
  };

  const handleDeleteTodoItem = async (original: IPunchlistTodoItem) => {
    const result = window.confirm('Are you sure you want to delete this item?');
    if (result) {
      setIsDeleting(true);
      try {
        await deletePunchlistItem(original.punchListItemId);

        await fetchPunchlistDetails(false, false);

        enqueueSnackbar(`You deleted a punchlist todo!`, {
          variant: 'success'
        });
      } catch (error: any) {
        enqueueSnackbar(error?.Detail ?? `Error deleting a punchlist todo, please try again.`, {
          variant: 'error'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const isPrint = useMediaQuery('print');

  const tableDataColumns = useMemo(() => {
    return [
      !isPrint && {
        Header: isPrint ? '' : 'Completed',
        accessor: 'itemType',
        sort: false,
        overrideWidth: (!isPrint && 150) ?? undefined,
        id: 'checkbox',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTodoItem } };
        }) => {
          return (
            <FormControlLabel
              color="primary"
              control={
                <Checkbox
                  disabled={isSaving[original.punchListItemId]}
                  checked={original.itemStatuses === 'Complete'}
                  id="orders-checkbox"
                  sx={{ margin: 0, padding: isMobile ? `4px 8px` : 'inherit' }}
                  onChange={(_, checked) => handleCheckboxComplete(checked, original)}
                />
              }
              label=""
            />
          );
        }
      },
      !isMobile &&
        !isPrint && {
          Header: 'Date Created',
          accessor: 'createdOn',
          overrideWidth: (!isPrint && 200) ?? undefined,
          sort: false,
          Cell: ({
            cell: {
              row: { original }
            }
          }: {
            cell: { row: { original: IPunchlistTodoItem } };
          }) => <span>{formatDate(original.createdOn)}</span>
        },
      {
        Header: 'Description',
        accessor: 'punchListItemDescription',
        sort: true,
        overrideWidth: isPrint && undefined,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTodoItem } };
        }) => {
          return (
            <>
              <Typography className="print--only" component="span">
                {original.description}
              </Typography>

              <Button
                style={
                  original.itemIsAssociatedToFwtOrChecklist
                    ? { color: theme.palette.error.main }
                    : null
                }
                sx={{
                  textAlign: 'left',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  textDecoration: original.itemStatuses === 'Complete' ? 'line-through' : 'auto',
                  padding: isPrint || isMobile ? '0' : 'inherit'
                }}
                onClick={() => {
                  handleTitleClick(original);
                }}
              >
                {isMobile ? truncString(original.description, 30) : original.description}
              </Button>
              <Typography component="span" sx={{ marginLeft: '5px', fontSize: 14 }}>
                {isSaving[original.punchListItemId] ? 'Saving...' : ''}
              </Typography>
            </>
          );
        }
      },
      !isMobile && {
        Header: 'Assigned Vendor',
        accessor: 'vendorName',
        overrideWidth: (!isPrint && 200) ?? undefined
      },
      !isMobile &&
        !isPrint && {
          Header: 'Icons',
          accessor: 'icons',
          overrideWidth: (!isPrint && 100) ?? undefined,
          Cell: ({
            cell: {
              row: { original }
            }
          }: {
            cell: { row: { original: IPunchlistTodoItem } };
          }) => {
            const hasFiles = original?.punchlistItemFiles?.length > 0;
            return (
              <Box display="flex" gap={0.25}>
                {hasFiles && (
                  <Tooltip title="Files attached">
                    <AttachFile
                      sx={{ color: theme => theme.palette.secondary.main }}
                      fontSize="small"
                    />
                  </Tooltip>
                )}
                {original?.hasComments && (
                  <Tooltip title="Comments posted">
                    <ChatBubble
                      sx={{ color: theme => theme.palette.secondary.main }}
                      fontSize="small"
                    />
                  </Tooltip>
                )}
              </Box>
            );
          }
        },
      {
        Header: 'Action',
        accessor: 'action',
        overrideWidth: !isPrint && !isMobile ? 300 : undefined,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTodoItem } };
        }) => {
          const params = new URLSearchParams();
          params.append('assignedTo', original.assignedTo);
          params.append('vendorId', `${original.partnerId}`);
          return (
            <span
              style={{
                paddingTop: isMobile ? '4px' : '0',
                display: 'inline-block'
              }}
            >
              <span style={{ fontWeight: 'bold' }}>{getActionTitle(original)}</span>{' '}
              <Link
                to={
                  route
                    ? `/vendors/punchlists?${params.toString()}`
                    : `/production/punchlists/todos?${params.toString()}`
                }
              >
                {original.completedByDisplayName || original.assignedToDisplayName}
              </Link>
            </span>
          );
        }
      },
      !isMobile && {
        Header: 'Due Date',
        accessor: 'dueDate',
        overrideWidth: (!isPrint && 200) ?? undefined,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTodoItem } };
        }) => <span>{formatDate(original.dueDate)}</span>
      },
      isAdmin && {
        Header: '',
        accessor: 'delete',
        overrideWidth: (!isPrint && 200) ?? undefined,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTodoItem } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="delete"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDeleteTodoItem(original)}
              >
                DELETE
              </Button>
              {original.itemStatuses !== 'Complete' && !hasAppliedFilters && (
                <IconButton color="inherit">
                  <DragIndicator />
                </IconButton>
              )}
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSaving,
    setPunchlistDetails,
    punchlistDetail,
    handleCheckboxComplete,
    handleDeleteTodoItem,
    isMobile,
    isAdmin,
    isPrint
  ]);
  const vendorNonCompletedTodoItems = useMemo(
    () =>
      punchlistDetail?.punchListItems?.filter(
        item => item.itemStatuses === 'AssignedTo' && item.vendorName === vendorName
      ),
    [punchlistDetail, vendorName]
  );

  const completedTodoItems = useMemo(
    () => punchlistDetail?.punchListItems?.filter(item => item.itemStatuses === 'Complete'),
    [punchlistDetail]
  );

  const tableData = useMemo(
    () =>
      vendorName && vendorName !== 'All'
        ? [...vendorNonCompletedTodoItems, ...completedTodoItems]
            ?.map(item => ({
              ...item,
              isDragDisabled: item.itemStatuses === 'Complete' || !allowedRole
            }))
            .filter(item => (route ? item.itemStatuses !== 'Complete' : true))
        : punchlistDetail?.punchListItems
            ?.map(item => ({
              ...item,
              isDragDisabled: item.itemStatuses === 'Complete' || !allowedRole
            }))
            .filter(item => (route ? item.itemStatuses !== 'Complete' : true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [punchlistDetail, vendorName, vendorNonCompletedTodoItems, completedTodoItems, allowedRole]
  );

  return (
    <Table
      columns={tableDataColumns}
      data={tableData}
      rowClasses={row => {
        // Don't show completed items on print
        return row.itemStatuses === 'Complete' ? 'print--none' : '';
      }}
      cellClasses={classes.cells}
      isLoading={false}
      isDragDisabled={hasAppliedFilters}
      onDragEnd={onDragEnd}
      hidePagination
      noResultsText="No to-dos"
      ResponsiveComponent={isMobile ? MobilePunchlistTodoList : undefined}
      mobileBreakPoint={1024}
      mobileProps={{
        includeDeleteInDetails: true,
        fields: tableDataColumns
          .filter(c => c.Header)
          .map(c => ({
            name: c.Header,
            accessor: c.accessor,
            Cell: c?.Cell
          })),
        handleDelete: (val: IPunchlistTodoItem) => handleDeleteTodoItem(val),
        handleTitleClick: (val: IPunchlistTodoItem) => handleTitleClick(val)
      }}
    />
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  cells: {
    '@media print': {
      padding: theme.spacing(0.15)
    }
  },
  rows: {
    '@media print': {
      padding: theme.spacing(0.25)
    }
  }
}));
